import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { FullPageStatus } from '../../components/FullPageStatus';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { useBusiness } from '../onboarding/useBusiness';
import { exchangeLinkedIn } from './integrationsApi';
import { knowledgeBaseSectionPath } from './sections';

/**
 * Back to the tab that started this, with the one-time notice param the section
 * reads and strips. `knowledgeBaseSectionPath` already carries a query string,
 * so this joins with `&`.
 */
const CONNECTED_PATH = `${knowledgeBaseSectionPath('integrations')}&linkedin=connected`;

/** Where every way out of this screen points. */
const INTEGRATIONS_PATH = knowledgeBaseSectionPath('integrations');

/** LinkedIn came back, but without the pair the exchange needs. */
const MISSING_PARAMS_MESSAGE =
  'LinkedIn did not send back what we need. Start again from Connected accounts.';

/** Message for the domain errors the exchange can answer with. */
function exchangeMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'INVALID_OAUTH_STATE':
        return 'This sign-in link is stale, start again from Connected accounts.';
      case 'INTEGRATION_DISABLED':
        return 'LinkedIn publishing is not configured on this server.';
      case 'LINKEDIN_UNAVAILABLE':
        return 'LinkedIn did not answer. Try connecting again in a minute.';
      default:
        break;
    }
    if (caught.status === 403) {
      return 'Only the account owner can connect LinkedIn.';
    }
  }
  return getErrorMessage(
    caught,
    'That connection did not finish. Start again from Connected accounts.',
  );
}

/**
 * The OAuth redirect target, `/integrations/linkedin/callback`.
 *
 * LinkedIn sends the user back here with `code` and `state` on the query string.
 * The redirect lands on the SPA rather than the API on purpose: this page posts
 * the pair through the normal authenticated client, so the `code` never touches
 * an unauthenticated route and membership is checked the same way as everywhere
 * else. The `state` is CSRF protection, not authentication — the session is.
 *
 * A query string, not a hash, so Supabase's `detectSessionInUrl` ignores it.
 *
 * The exchange is fired exactly once per mount: it consumes a single-use code,
 * so React's development double-invoke would otherwise spend it on the first
 * call and report the second as a failure.
 */
export function LinkedInCallbackPage() {
  const [searchParams] = useSearchParams();
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();

  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptedRef = useRef(false);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const deniedCode = searchParams.get('error');
  const deniedDetail = searchParams.get('error_description');

  // Whether the callback carries a usable pair is a fact about the URL, so it
  // is read during render rather than discovered in an effect.
  const hasCallbackParams = code !== null && state !== null;

  useEffect(() => {
    // The user said no at LinkedIn, the URL is unusable, or the account context
    // is not ready yet.
    if (deniedCode !== null || !hasCallbackParams || !businessId) return;
    if (attemptedRef.current) return;
    if (code === null || state === null) return;

    attemptedRef.current = true;

    void (async () => {
      try {
        await exchangeLinkedIn(businessId, { code, state });
        setIsDone(true);
      } catch (caught) {
        setError(exchangeMessage(caught));
      }
    })();
  }, [businessId, code, deniedCode, hasCallbackParams, state]);

  if (isDone) {
    return <Navigate to={CONNECTED_PATH} replace />;
  }

  if (deniedCode !== null) {
    return (
      <main className="page">
        <div className="kb-section">
          <p className="form__notice">
            LinkedIn did not grant access.{' '}
            {deniedDetail ? `${deniedDetail}.` : 'Nothing was connected and nothing changed.'}
          </p>
          <div className="page__actions">
            <Link className="button button--primary" to={INTEGRATIONS_PATH}>
              Back to Connected accounts
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (error !== null || !hasCallbackParams) {
    return (
      <main className="page">
        <div className="kb-section">
          <p className="form__error" role="alert">
            {error ?? MISSING_PARAMS_MESSAGE}
          </p>
          <div className="page__actions">
            <Link className="button button--primary" to={INTEGRATIONS_PATH}>
              Back to Connected accounts
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!isBusinessLoading && !businessId) {
    return (
      <main className="page">
        <div className="kb-section">
          <p className="form__notice">
            {businessError ??
              'No business is linked to this account yet. Finish setup and connect LinkedIn from the brand profile.'}
          </p>
          <div className="page__actions">
            <Link className="button button--primary" to="/onboarding">
              Finish setup
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return <FullPageStatus message="Connecting your LinkedIn account" />;
}
